import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import CrudModal from "../../components/CrudModal";
import { toast } from "sonner";
import { Eye, Warning, Check, ShieldCheck, MicrophoneStage, PencilSimple, Trash, Plus } from "@phosphor-icons/react";

const STATUS_CLS = {
  Clean: "bg-emerald-50 text-emerald-700",
  Review: "bg-amber-50 text-amber-800",
  "High Risk": "bg-rose-50 text-rose-700",
};

const FIELDS = [
  { name: "candidate", label: "Candidate", type: "text" },
  { name: "exam", label: "Exam", type: "text" },
  { name: "risk", label: "Risk score (0-100)", type: "number" },
  { name: "flags", label: "Flags (comma-separated)", type: "text", hint: "e.g. Multiple faces, Tab switch" },
  { name: "status", label: "Status", type: "select", options: [
    { value: "Clean", label: "Clean" }, { value: "Review", label: "Review" }, { value: "High Risk", label: "High Risk" },
  ]},
];

export default function Proctoring() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/proctoring/sessions");
    setRows(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setModal({ open: true, editing: null });
  const openEdit = (r) => setModal({
    open: true,
    editing: { ...r, flags: Array.isArray(r.flags) ? r.flags.join(", ") : r.flags },
  });
  const close = () => setModal({ open: false, editing: null });

  const save = async (values) => {
    const body = {
      ...values,
      flags: typeof values.flags === "string" ? values.flags.split(",").map(s => s.trim()).filter(Boolean) : values.flags,
    };
    try {
      if (modal.editing) {
        await api.patch(`/proctoring/sessions/${modal.editing.session_id}`, body);
        toast.success("Session updated");
      } else {
        await api.post("/proctoring/sessions", body);
        toast.success("Session created");
      }
      close(); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete session for ${r.candidate}?`)) return;
    try { await api.delete(`/proctoring/sessions/${r.session_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <PageHeader
        overline="M11 · Assessment"
        title={t("modules.proctoring.title")}
        description={t("modules.proctoring.subtitle")}
        action={
          <Button onClick={openCreate} className="rounded-md bg-[hsl(244_53%_20%)] hover:bg-[hsl(244_53%_15%)]" data-testid="new-proctor-btn">
            <Plus size={14} className="mr-1.5"/> New session
          </Button>
        }
      />
      <div className="p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border rounded-md overflow-hidden">
          {[
            { I: ShieldCheck, l: "Sessions live", v: rows.length, t: "exams in progress" },
            { I: Eye, l: "Faces tracked", v: "1.2M", t: "frames analyzed" },
            { I: MicrophoneStage, l: "Audio flags", v: rows.reduce((a, r) => a + (r.flags?.some(f => f.toLowerCase().includes("audio")) ? 1 : 0), 0), t: "today" },
            { I: Warning, l: "High risk", v: rows.filter(r => r.status === "High Risk").length, t: "needs review" },
          ].map(s => (
            <div key={s.l} className="bg-white p-5">
              <s.I size={20} weight="duotone" className="text-[hsl(244_53%_20%)]"/>
              <div className="eos-overline mt-3">{s.l}</div>
              <div className="font-heading font-extrabold text-3xl mt-1 tracking-tight">{s.v}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{s.t}</div>
            </div>
          ))}
        </div>

        <div className="eos-card p-0 overflow-hidden">
          <div className="p-4 border-b border-border font-heading font-semibold">Recent sessions</div>
          <div className="divide-y divide-border">
            {rows.map(r => (
              <div key={r.session_id} className="p-4 grid grid-cols-1 md:grid-cols-12 items-center gap-3" data-testid={`proctor-${r.session_id}`}>
                <div className="md:col-span-3">
                  <div className="font-medium text-sm">{r.candidate}</div>
                  <div className="text-[11px] text-muted-foreground">{r.exam}</div>
                </div>
                <div className="md:col-span-3 text-xs text-muted-foreground">
                  {r.flags?.map((f) => <div key={f}>· {f}</div>)}
                </div>
                <div className="md:col-span-3">
                  <div className="text-[11px] text-muted-foreground">Risk score</div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="h-1.5 flex-1 bg-border rounded-full overflow-hidden">
                      <div className={`h-full ${r.risk > 60 ? "bg-rose-500" : r.risk > 30 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${r.risk}%` }}/>
                    </div>
                    <span className="font-mono-eos text-xs">{r.risk}</span>
                  </div>
                </div>
                <div className="md:col-span-2">
                  <span className={`text-[11px] px-2 py-1 rounded-md inline-flex items-center gap-1 ${STATUS_CLS[r.status]}`}>
                    {r.status === "Clean" ? <Check size={11}/> : <Warning size={11}/>} {r.status}
                  </span>
                </div>
                <div className="md:col-span-1 flex items-center justify-end gap-1">
                  <Button variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 p-0 rounded" title="Edit" data-testid={`edit-${r.session_id}`}><PencilSimple size={13}/></Button>
                  <Button variant="ghost" onClick={() => remove(r)} className="h-7 w-7 p-0 rounded text-rose-600 hover:text-rose-700 hover:bg-rose-50" title="Delete" data-testid={`delete-${r.session_id}`}><Trash size={13}/></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <CrudModal
        open={modal.open}
        onClose={close}
        title={modal.editing ? "Edit proctoring session" : "New proctoring session"}
        fields={FIELDS}
        initial={modal.editing || { status: "Clean", risk: 0, flags: "" }}
        onSubmit={save}
        submitLabel={modal.editing ? "Save changes" : "Create session"}
      />
    </div>
  );
}
